import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Assignment, Group, Lesson } from '@/hooks/useDashboardData';
import { assignmentStatusMap, getStatusMeta } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import AssignmentModal from './components/AssignmentModal';
import SubmissionsModal, { type Submission, type StudentRecord } from './components/SubmissionsModal';

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Odevler() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [students, setStudents] = useState<StudentRecord[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [studentNames, setStudentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editAssignment, setEditAssignment] = useState<Assignment | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Assignment | null>(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: assignmentsData, error: assignmentsError },
        { data: groupsData, error: groupsError },
        { data: lessonsData, error: lessonsError },
        { data: studentsData, error: studentsError },
        { data: profilesData, error: profilesError },
        { data: submissionsData, error: submissionsError },
      ] = await Promise.all([
        supabase.from('zehrahoca_assignments').select('*').order('deadline', { ascending: true }),
        supabase.from('zehrahoca_groups').select('*').eq('active', true).order('created_at', { ascending: true }),
        supabase.from('zehrahoca_lessons').select('*').order('date', { ascending: true }),
        supabase.from('zehrahoca_students').select('id, group_id, parent_name'),
        supabase.from('zehrahoca_profiles').select('id, full_name').eq('role', 'ogrenci'),
        supabase.from('zehrahoca_assignment_submissions').select('*').order('submitted_at', { ascending: false }),
      ]);

      if (assignmentsError) throw assignmentsError;
      if (groupsError) throw groupsError;
      if (lessonsError) throw lessonsError;
      if (studentsError) throw studentsError;
      if (profilesError) throw profilesError;
      if (submissionsError) throw submissionsError;

      const names: Record<string, string> = {};
      (profilesData ?? []).forEach((p: { id: string; full_name: string | null }) => {
        names[p.id] = p.full_name ?? '';
      });

      setAssignments((assignmentsData ?? []) as Assignment[]);
      setGroups((groupsData ?? []) as Group[]);
      setLessons((lessonsData ?? []) as Lesson[]);
      setStudents((studentsData ?? []) as StudentRecord[]);
      setSubmissions((submissionsData ?? []) as Submission[]);
      setStudentNames(names);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const groupName = (id: string): string => groups.find((g) => g.id === id)?.name ?? '-';
  const submissionsFor = (assignmentId: string): number =>
    submissions.filter((s) => s.assignment_id === assignmentId).length;
  const studentsFor = (groupId: string): number =>
    students.filter((s) => s.group_id === groupId).length;

  const submissionsForSelected = selectedAssignment
    ? submissions.filter((s) => s.assignment_id === selectedAssignment.id)
    : [];

  async function handleSeedDemo() {
    setSeedingDemo(true);
    setSeedMsg(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('zehrahoca-seed-demo', { body: {} });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      setSeedMsg('Demo öğrenciler ve teslimler eklendi.');
      await fetchAll();
    } catch (err) {
      setSeedMsg((err as Error).message || 'Demo veriler yüklenirken bir hata oluştu.');
    } finally {
      setSeedingDemo(false);
    }
  }

  function handleNew() {
    setEditAssignment(null);
    setModalOpen(true);
  }

  function handleEdit(a: Assignment) {
    setEditAssignment(a);
    setModalOpen(true);
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      const { data: subs } = await supabase
        .from('zehrahoca_assignment_submissions')
        .select('id')
        .eq('assignment_id', confirmDelete.id);
      const subIds = (subs ?? []).map((s: { id: string }) => s.id);
      if (subIds.length > 0) {
        await supabase.from('zehrahoca_assignment_submissions').delete().in('id', subIds);
      }
      const { error: delError } = await supabase
        .from('zehrahoca_assignments')
        .delete()
        .eq('id', confirmDelete.id);
      if (delError) throw delError;
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDelete(null);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ödevler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Ödevler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={fetchAll}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ödevler</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Öğrencilere gönderdiğiniz ödevleri görüntüleyin ve teslimleri kontrol edin.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          Yeni Ödev Ekle
        </button>
      </div>

      {students.length === 0 && (
        <div className="flex flex-col gap-3 rounded-xl border border-accent-200 bg-accent-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-lightbulb-line text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Henüz kayıtlı öğrenci yok</p>
              <p className="mt-0.5 text-xs text-foreground-600">
                Teslim takibini denemek için demo öğrenciler ve örnek teslimler yükleyebilirsiniz.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSeedDemo}
            disabled={seedingDemo}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-accent-300 bg-white px-4 py-2 text-sm font-semibold text-accent-800 transition hover:bg-accent-100 disabled:opacity-70 cursor-pointer"
          >
            <i className={`${seedingDemo ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'}`} />
            {seedingDemo ? 'Yükleniyor...' : 'Demo Verileri Yükle'}
          </button>
          {seedMsg && <p className="text-xs text-foreground-600">{seedMsg}</p>}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/60 text-xs text-foreground-500">
                <th className="px-4 py-3 font-semibold">Ödev</th>
                <th className="px-4 py-3 font-semibold">Grup</th>
                <th className="px-4 py-3 font-semibold">Son Teslim</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 font-semibold">Teslim</th>
                <th className="px-4 py-3 text-right font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-foreground-500">
                    Henüz ödev eklenmemiş. İlk ödevinizi oluşturmak için "Yeni Ödev Ekle" butonuna tıklayın.
                  </td>
                </tr>
              )}
              {assignments.map((a) => {
                const submitted = submissionsFor(a.id);
                const total = studentsFor(a.group_id);
                return (
                  <tr key={a.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-4 py-3 font-medium text-foreground-900">
                      <div className="flex flex-col gap-1">
                        <span>{a.title}</span>
                        {a.file_url ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                            <i className="ri-file-pdf-2-line text-sm text-emerald-600" />
                            Dosya var
                            <a
                              href={a.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-1 inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-eye-line" /> Aç
                            </a>
                            <a
                              href={a.file_url}
                              download
                              className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-download-2-line" /> İndir
                            </a>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-400">
                            <i className="ri-file-info-line text-sm" /> Dosya yok
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-700">{groupName(a.group_id)}</td>
                    <td className="px-4 py-3 text-foreground-600">{formatDate(a.deadline)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge meta={getStatusMeta(a.status, assignmentStatusMap)} />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
                          total === 0
                            ? 'bg-background-100 text-foreground-400'
                            : submitted === 0
                              ? 'bg-amber-100 text-amber-700'
                              : submitted < total
                                ? 'bg-primary-100 text-primary-700'
                                : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {total === 0 ? 'Öğrenci yok' : `${submitted}/${total} teslim`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setSelectedAssignment(a)}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-background-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-eye-line" />
                          Detay
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(a)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200 cursor-pointer"
                          title="Düzenle"
                        >
                          <i className="ri-edit-line text-base" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(a)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 cursor-pointer"
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AssignmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchAll}
        groups={groups}
        lessons={lessons}
        students={students}
        studentNames={studentNames}
        assignment={editAssignment}
      />

      <SubmissionsModal
        open={selectedAssignment !== null}
        onClose={() => setSelectedAssignment(null)}
        assignment={selectedAssignment}
        submissions={submissionsForSelected}
        students={students}
        studentNames={studentNames}
        groupName={selectedAssignment ? groupName(selectedAssignment.group_id) : ''}
        onChanged={fetchAll}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Ödevi Sil"
        subtitle="Bu işlem geri alınamaz."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.title}</span> ödevini silmek istediğinize emin misiniz?
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}